import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'pt', label: 'Português' },
  { code: 'it', label: 'Italiano' },
  { code: 'zh', label: '中文' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'ru', label: 'Русский' },
  { code: 'ar', label: 'العربية' },
];

export function LanguageSelector({ placement = 'bottom-end' }: { placement?: 'bottom-end' | 'top-start' } = {}) {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedLanguage = LANGUAGES.find((lang) => lang.code === language) || LANGUAGES[0];
  const popoverPositionClass = placement === 'top-start' ? 'left-0 bottom-11' : 'right-0 top-11';

  useEffect(() => {
    if (!isOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', closeOnEscape);
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.removeEventListener('mousedown', closeOnOutsideClick);
    };
  }, [isOpen]);

  const handleSelect = (code: string) => {
    setLanguage(code);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative flex justify-end mb-2">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="inline-flex h-9 min-w-[132px] items-center justify-between gap-2 rounded-full border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 shadow-sm transition hover:border-zinc-300 hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-blue-200"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{selectedLanguage.label}</span>
        <svg className="h-4 w-4 text-zinc-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Mobile: bottom sheet with backdrop. */}
          <div className="fixed inset-0 z-[1000] sm:hidden">
            <button
              type="button"
              className="absolute inset-0 bg-zinc-950/35"
              aria-label="Close language menu"
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute inset-x-3 bottom-3 overflow-hidden rounded-3xl border border-zinc-200 bg-white shadow-2xl">
              <LanguageOptions language={language} onSelect={handleSelect} />
            </div>
          </div>

          {/* Desktop/tablet: anchored popover, no page overlay. */}
          <div
            className={`absolute ${popoverPositionClass} z-50 hidden w-64 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-xl sm:block`}
          >
            <LanguageOptions language={language} onSelect={handleSelect} />
          </div>
        </>
      )}
    </div>
  );
}

function LanguageOptions({ language, onSelect }: { language: string; onSelect: (code: string) => void }) {
  return (
    <>
      <div className="border-b border-zinc-100 px-4 py-3">
        <p className="text-sm font-semibold text-zinc-900">Choose language</p>
      </div>
      <div className="max-h-[60vh] overflow-y-auto p-2" role="listbox" aria-label="Choose language">
        {LANGUAGES.map(({ code, label }) => {
          const selected = code === language;
          return (
            <button
              key={code}
              type="button"
              role="option"
              aria-selected={selected}
              onClick={() => onSelect(code)}
              className={`flex w-full items-center justify-between rounded-xl px-3 py-3 text-left text-sm transition ${
                selected ? 'bg-blue-50 text-blue-700' : 'text-zinc-700 hover:bg-zinc-50'
              }`}
            >
              <span>{label}</span>
              {selected && (
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path
                    fillRule="evenodd"
                    d="M16.704 5.29a1 1 0 010 1.415l-7.2 7.2a1 1 0 01-1.415 0l-3-3a1 1 0 111.415-1.414l2.292 2.292 6.493-6.493a1 1 0 011.415 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}
